import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Search, RefreshCw, ExternalLink } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pending_payment", label: "Pendiente pago" },
  { value: "preparing", label: "Preparando" },
  { value: "shipped", label: "En camino" },
  { value: "delivered", label: "Entregado" },
  { value: "cancelled", label: "Cancelado" },
];

const STATUS_COLORS = {
  pending_payment: "text-white/50",
  preparing: "text-arre-red",
  shipped: "text-orange-400",
  delivered: "text-green-400",
  cancelled: "text-white/30",
};
const STATUS_LABELS = {
  pending_payment: "Pendiente pago",
  preparing: "Preparando",
  shipped: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    if (q) params.append("q", q);
    api.get(`/admin/orders?${params.toString()}`).then((r) => {
      setOrders(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderNumber, status, notifyWA = false) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderNumber}/status`, { status, notify_whatsapp: notifyWA });
      if (data.whatsapp_url && notifyWA) {
        window.open(data.whatsapp_url, "_blank");
      }
      load();
      if (selectedOrder?.order_number === orderNumber) {
        setSelectedOrder(data.order);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Error al actualizar");
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-orders">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-2">⟡ Admin</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase text-white leading-none">Pedidos</h1>
        </div>
        <button onClick={load} className="btn-arre border border-arre-border hover:border-arre-red text-white font-display text-sm tracking-widest uppercase px-4 py-2 inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Refrescar
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por número, correo, teléfono o nombre..."
            className="w-full bg-arre-surface border border-arre-border pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-arre-red"
            data-testid="admin-order-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-arre-surface border border-arre-border px-4 py-2.5 text-white text-sm focus:outline-none focus:border-arre-red font-display tracking-widest uppercase"
          data-testid="admin-order-status-filter"
        >
          {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
        </select>
      </div>

      <div className="bg-arre-surface border border-arre-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-arre-border text-[10px] tracking-[0.2em] uppercase text-white/50">
              <th className="text-left p-4">Orden</th>
              <th className="text-left p-4">Cliente</th>
              <th className="text-left p-4">Total</th>
              <th className="text-left p-4">Pago</th>
              <th className="text-left p-4">Estado</th>
              <th className="text-left p-4">Fecha</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-8 text-center text-white/50">Cargando...</td></tr>}
            {!loading && orders.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-white/50">Sin pedidos que mostrar</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.order_number} className="border-b border-arre-border/50 hover:bg-arre-black/40 transition">
                <td className="p-4 font-display text-base text-white">{o.order_number}</td>
                <td className="p-4 text-white/80">
                  <div>{o.shipping?.name}</div>
                  <div className="text-xs text-white/50">{o.shipping?.email}</div>
                </td>
                <td className="p-4 font-display text-base text-white">{fmtMXN(o.total)}</td>
                <td className="p-4">
                  <span className={o.payment_status === "paid" ? "text-green-400" : "text-white/40"}>
                    {o.payment_status === "paid" ? "Pagado" : "Pendiente"}
                  </span>
                </td>
                <td className={`p-4 font-bold ${STATUS_COLORS[o.order_status] || ""}`}>
                  {STATUS_LABELS[o.order_status] || o.order_status}
                </td>
                <td className="p-4 text-white/60 text-xs">
                  {new Date(o.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </td>
                <td className="p-4">
                  <button
                    onClick={() => setSelectedOrder(o)}
                    className="text-arre-red hover:text-white text-xs tracking-widest uppercase font-bold inline-flex items-center gap-1"
                    data-testid={`view-order-${o.order_number}`}
                  >
                    Ver <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-detail-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedOrder(null)} />
          <div className="relative w-full max-w-2xl bg-arre-black border border-arre-border max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-arre-surface border-b border-arre-border p-6 flex items-start justify-between">
              <div>
                <div className="text-xs tracking-widest uppercase text-arre-red">Pedido</div>
                <div className="font-display text-3xl tracking-wider text-white">{selectedOrder.order_number}</div>
                <div className="text-sm text-white/60 mt-1">
                  {new Date(selectedOrder.created_at).toLocaleString("es-MX")}
                </div>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="text-white/60 hover:text-white text-2xl">×</button>
            </div>
            <div className="p-6 space-y-6">
              <section>
                <h3 className="font-display text-xl tracking-wider uppercase text-white mb-3">Cliente</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-white/50">Nombre:</span> <span className="text-white">{selectedOrder.shipping?.name}</span></div>
                  <div><span className="text-white/50">Correo:</span> <span className="text-white">{selectedOrder.shipping?.email}</span></div>
                  <div><span className="text-white/50">Teléfono:</span> <span className="text-white">{selectedOrder.shipping?.phone}</span></div>
                </div>
                <div className="text-sm text-white/70 mt-2">
                  <span className="text-white/50">Dirección:</span> {selectedOrder.shipping?.street}, {selectedOrder.shipping?.city}, {selectedOrder.shipping?.state} CP {selectedOrder.shipping?.zip}
                </div>
                {selectedOrder.shipping?.notes && (
                  <div className="mt-2 text-sm text-white/60 italic">Notas: {selectedOrder.shipping.notes}</div>
                )}
              </section>
              <section>
                <h3 className="font-display text-xl tracking-wider uppercase text-white mb-3">Productos</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((it, i) => (
                    <div key={i} className="flex justify-between items-center text-sm border-b border-arre-border/50 py-2">
                      <div>
                        <div className="text-white">{it.name}</div>
                        <div className="text-xs text-white/40">x{it.quantity} · {it.type}</div>
                      </div>
                      <div className="text-white font-display">{fmtMXN(it.line_total)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-arre-border space-y-1 text-sm">
                  <div className="flex justify-between text-white/70"><span>Subtotal</span><span>{fmtMXN(selectedOrder.subtotal)}</span></div>
                  {selectedOrder.discount > 0 && (
                    <div className="flex justify-between text-arre-red"><span>Descuento {selectedOrder.is_byop ? "(BYOP 15%)" : ""}{selectedOrder.coupon?.code ? ` +${selectedOrder.coupon.code}` : ""}</span><span>-{fmtMXN(selectedOrder.discount)}</span></div>
                  )}
                  <div className="flex justify-between text-white/70"><span>Envío</span><span>{fmtMXN(selectedOrder.shipping_fee)}</span></div>
                  <div className="flex justify-between text-white font-display text-xl mt-2"><span>TOTAL</span><span>{fmtMXN(selectedOrder.total)}</span></div>
                </div>
              </section>
              <section>
                <h3 className="font-display text-xl tracking-wider uppercase text-white mb-3">Cambiar estado</h3>
                <div className="grid grid-cols-2 gap-2">
                  {["preparing", "shipped", "delivered", "cancelled"].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selectedOrder.order_number, s, true)}
                      disabled={selectedOrder.order_status === s}
                      className={`btn-arre font-display text-xs tracking-widest uppercase px-3 py-2.5 border ${
                        selectedOrder.order_status === s
                          ? "border-arre-red bg-arre-red/20 text-arre-red"
                          : "border-arre-border text-white hover:border-arre-red"
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                      data-testid={`status-btn-${s}`}
                    >
                      {STATUS_LABELS[s]}
                      {selectedOrder.order_status !== s && <span className="block text-[9px] text-white/40 mt-0.5">+ WhatsApp</span>}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-white/40 mt-3">
                  Cambiar estado abre automáticamente un WhatsApp pre-redactado al cliente con la actualización.
                </p>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
